import { Stack, Tooltip, Typography } from "@mui/material";
import {
  IconAlertTriangle,
  IconBike,
  IconCar,
  IconChecks,
  IconClock,
  IconFaceId,
  IconIdOff,
  IconLinkOff,
  IconLock,
  IconLockOpen,
  IconMotorbike,
  IconRun,
  IconSailboat,
  IconSubscript,
  IconUserCheck,
  IconUserExclamation,
  IconUserStar,
} from "@tabler/icons-react";
import _ from "lodash";
import { FormattedMessage } from "react-intl";
import Chip from "ui-component/extended/Chip";
import { v4 as uuidv4 } from "uuid";
import {
  findCorrespondingUnitPrice,
  fromBackToFrontPackage,
  fromFrontToBackPackage,
} from "views/packages/utils";

export const formatBytes = (size_in_bytes, decimals = 2) => {
  if (size_in_bytes === 0) return "0";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Octets", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(size_in_bytes) / Math.log(k));
  return (
    parseFloat((size_in_bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
  );
};

export const resolveImageURI = (photoKey) => {
  return `${process.env.REACT_APP_CLOUDFRONT_URL}/minified/${photoKey}`;
};

export const bibFromSuperAthlete = (superAthlete) => {
  if (superAthlete.first_name) {
    return `${superAthlete.first_name[0]}.${superAthlete.last_name}`;
  }
  return superAthlete.last_name;
};

const GENDER = {
  Male: "Homme",
  Female: "Femme",
};

const AWS_STATUS = {
  all: {
    text: <FormattedMessage id="photo.aws_status.all" />,
    chip: (
      <Chip
        sx={{ textTransform: "uppercase", fontWeight: "bold" }}
        chipcolor="primary"
        label={<FormattedMessage id="photo.aws_status.all" />}
      />
    ),
    miniChip: (
      <Chip
        sx={{ textTransform: "uppercase", fontWeight: "bold" }}
        chipcolor="primary"
        label=""
      />
    ),
  },
  presigned: {
    text: <FormattedMessage id="photo.aws_status.presigned" />,
    chip: (
      <Chip
        sx={{ textTransform: "uppercase", fontWeight: "bold" }}
        chipcolor="warning.dark"
        size="small"
        icon={<IconClock size={16} color="orange" />}
        label={<FormattedMessage id="photo.aws_status.presigned" />}
      />
    ),
    miniChip: <IconClock size={16} color="orange" />,
  },
  errored: {
    text: <FormattedMessage id="photo.aws_status.errored" />,
    chip: (
      <Chip
        sx={{ textTransform: "uppercase", fontWeight: "bold" }}
        chipcolor="error"
        size="small"
        icon={<IconAlertTriangle size={16} color="red" />}
        label={<FormattedMessage id="photo.aws_status.errored" />}
      />
    ),
    miniChip: <IconAlertTriangle size={16} color="red" />,
  },
  analyzed: {
    text: <FormattedMessage id="photo.aws_status.analyzed" />,
    chip: (
      <Chip
        sx={{ textTransform: "uppercase", fontWeight: "bold" }}
        chipcolor="success"
        icon={<IconChecks size={16} color="green" />}
        size={"small"}
        label={<FormattedMessage id="photo.aws_status.analyzed" />}
      />
    ),
    miniChip: <IconChecks size={16} color="green" />,
  },
};

const IDENTIFICATION_STATUS = {
  NO_ONE: {
    text: <FormattedMessage id="photo.identification_status.no_one" />,
    chip: (
      <Chip
        sx={{ textTransform: "uppercase", fontWeight: "bold" }}
        size="small"
        chipcolor="error"
        icon={<IconUserExclamation size={16} />}
        label={<FormattedMessage id="photo.identification_status.no_one" />}
      />
    ),
  },
  INCORRECT: {
    text: <FormattedMessage id="photo.identification_status.incorrect" />,
    chip: (
      <Chip
        sx={{ textTransform: "uppercase", fontWeight: "bold" }}
        size="small"
        chipcolor="warning"
        icon={<IconUserExclamation size={16} />}
        label={<FormattedMessage id="photo.identification_status.incorrect" />}
      />
    ),
  },
  CORRECT: {
    text: <FormattedMessage id="photo.identification_status.correct" />,
    chip: (
      <Chip
        sx={{ textTransform: "uppercase", fontWeight: "bold" }}
        icon={<IconUserCheck size={16} />}
        size={"small"}
        chipcolor={"success"}
        label={<FormattedMessage id="photo.identification_status.correct" />}
      />
    ),
  },
};

const ALGO_SPORT = {
  running: {
    description: (
      <FormattedMessage id="import_job.algo_sport.running.description" />
    ),
    title: <FormattedMessage id="import_job.algo_sport.running.title" />,
    icon: <IconRun size={16} />,
  },
  bmx: {
    description: (
      <FormattedMessage id="import_job.algo_sport.bmx.description" />
    ),
    title: <FormattedMessage id="import_job.algo_sport.bmx.title" />,
    icon: <IconBike size={16} />,
  },
  motorcycle: {
    description: (
      <FormattedMessage id="import_job.algo_sport.motorcycle.description" />
    ),
    title: <FormattedMessage id="import_job.algo_sport.motorcycle.title" />,
    icon: <IconMotorbike size={16} />,
  },
  car: {
    description: (
      <FormattedMessage id="import_job.algo_sport.car.description" />
    ),
    title: <FormattedMessage id="import_job.algo_sport.car.title" />,
    icon: <IconCar size={16} />,
  },
  without_bibs: {
    description: (
      <FormattedMessage id="import_job.algo_sport.without_bibs.description" />
    ),
    title: <FormattedMessage id="import_job.algo_sport.without_bibs.title" />,
    icon: <IconFaceId size={16} />,
  },
  super_athletes: {
    description: (
      <FormattedMessage id="import_job.algo_sport.super_athletes.description" />
    ),
    title: <FormattedMessage id="import_job.algo_sport.super_athletes.title" />,
    icon: <IconUserStar size={16} />,
  },
  boat: {
    description: (
      <FormattedMessage id="import_job.algo_sport.boat.description" />
    ),
    title: <FormattedMessage id="import_job.algo_sport.boat.title" />,
    icon: <IconSailboat size={16} />,
  },
  no_rekognition: {
    description: (
      <FormattedMessage id="import_job.algo_sport.no_rekognition.description" />
    ),
    title: <FormattedMessage id="import_job.algo_sport.no_rekognition.title" />,
    icon: <IconIdOff size={16} />,
  },
};

export const ALBUM_ACCESSIBILITY = {
  opened: {
    icon: <IconLockOpen size={16} />,
    short: <FormattedMessage id="album.privacy.opened.title" />,
    description: <FormattedMessage id="album.privacy.opened.description" />,
    color: "success",
  },
  closed: {
    icon: <IconLock size={16} />,
    short: <FormattedMessage id="album.privacy.closed.title" />,
    description: <FormattedMessage id="album.privacy.closed.description" />,
    color: "error",
  },
  subscription: {
    icon: <IconSubscript size={16} />,
    short: <FormattedMessage id="album.privacy.subscription.title" />,
    description: (
      <FormattedMessage id="album.privacy.subscription.description" />
    ),
    color: "warning",
  },
  not_indexed: {
    icon: <IconLinkOff size={16} />,
    short: <FormattedMessage id="album.privacy.not_indexed.title" />,
    description: (
      <FormattedMessage id="album.privacy.not_indexed.description" />
    ),
    color: "info",
  },
  selfie_only: {
    icon: <IconFaceId size={16} />,
    short: <FormattedMessage id="album.privacy.selfie_only.title" />,
    description: (
      <FormattedMessage id="album.privacy.selfie_only.description" />
    ),
    color: "info",
  },
};

export const algoSportIcon = (sportAlgo) => {
  if (Object.keys(ALGO_SPORT).includes(sportAlgo.toLowerCase())) {
    return ALGO_SPORT[sportAlgo].icon;
  }
  return "";
};

export const algoSportChip = (sportAlgo) => {
  if (Object.keys(ALGO_SPORT).includes(sportAlgo.toLowerCase())) {
    const algoSport = ALGO_SPORT[sportAlgo.toLowerCase()];
    const uuid = uuidv4();

    return (
      <Chip
        title={algoSport.description}
        key={uuid}
        label={algoSport.title}
        disabled={true}
        size="small"
        sx={{ fontSize: "0.50rem", textTransform: "uppercase" }}
        icon={algoSport.icon}
      />
    );
  }
  return "";
};

export const algoSportText = (sportAlgo) => {
  if (Object.keys(ALGO_SPORT).includes(sportAlgo.toLowerCase())) {
    const algoSport = ALGO_SPORT[sportAlgo.toLowerCase()];
    return (
      <Tooltip title={algoSport.description} arrow>
        <Typography>{algoSport.title}</Typography>
      </Tooltip>
    );
  }
  return "";
};

export const algoSportDescription = (sportAlgo) => {
  if (Object.keys(ALGO_SPORT).includes(sportAlgo.toLowerCase())) {
    return ALGO_SPORT[sportAlgo.toLowerCase()].description;
  }
  return "";
};

export const displayName = (last_name, first_name) => {
  if (first_name) {
    // Capitalize first letter of last name and first letter of first name
    return `${first_name.charAt(0).toUpperCase()}${first_name.slice(
      1
    )} ${last_name.charAt(0).toUpperCase()}${last_name.slice(1)}`;
  }
  return `${last_name.charAt(0).toUpperCase()}${last_name.slice(1)}`;
};

export const awsStatusText = (status) => {
  if (Object.keys(AWS_STATUS).includes(status)) {
    return AWS_STATUS[status].text;
  }
  return "";
};

export const awsStatusChip = (status) => {
  if (Object.keys(AWS_STATUS).includes(status)) {
    return AWS_STATUS[status].chip;
  }
  return "";
};

export const awsStatusMiniChip = (status) => {
  if (Object.keys(AWS_STATUS).includes(status)) {
    return AWS_STATUS[status].miniChip;
  }
  return "";
};

export const identificationStatusChip = (idStatus) => {
  if (Object.keys(IDENTIFICATION_STATUS).includes(idStatus)) {
    return IDENTIFICATION_STATUS[idStatus].chip;
  }
  return "";
};

export const identificationStatusText = (idStatus) => {
  if (Object.keys(IDENTIFICATION_STATUS).includes(idStatus)) {
    return IDENTIFICATION_STATUS[idStatus].text;
  }
  return "";
};

export const genderText = (value) => {
  if (Object.keys(GENDER).includes(value)) {
    return GENDER[value];
  }
  return "N/A";
};

export const renderPrice = (priceInCent) => {
  if (priceInCent == null) {
    return "";
  }
  return priceInCent / 100;
};

export const fromFrontToBackAlbum = (album) => {
  console.log("fromFrontToBackAlbum", album);
  const album_packages = album.album_packages.map((pa) =>
    fromFrontToBackPackage(pa)
  );
  return {
    ...album,
    sport_name: "event",
    pack_athlete_unit_price: album.pack_athlete_unit_price
      ? album.pack_athlete_unit_price * 100
      : null,
    album_packages: album_packages,
  };
};

export const fromBackToFrontAlbum = (album) => {
  const album_packages = album.album_packages
    .sort((a, b) => (a.is_download ? -1 : 1))
    .map((pa) => fromBackToFrontPackage(pa));
  return {
    ...album,
    pack_athlete_unit_price: album.pack_athlete_unit_price
      ? album.pack_athlete_unit_price / 100
      : null,
    album_packages: album_packages,
  };
};

export const calculateTotalPrices = (photos, album) => {
  // We have 3 types of products: photo, athlete and selfie
  const partition = _.partition(photos, (p) => p.type === "photo");
  const photosOnly = partition[0];
  const otherProductsOnly = partition[1];
  const productsByPackage = _.groupBy(
    photosOnly.filter((p) => p.package_id !== null),
    (p) => p.package_id
  );
  let packages = [];
  _.forOwn(productsByPackage, (products, key) => {
    const quantity = products.reduce((acc, p) => acc + p.quantity, 0);
    const packagee = products[0].package;
    const unitPriceEntity = findCorrespondingUnitPrice(packagee, quantity);
    packages.push({
      package: packagee,
      packageQuantity: quantity,
      packageUnitPrice: unitPriceEntity,
      packageTotal: quantity * unitPriceEntity.unit_price,
    });
  });

  const qtyPackAthlete = otherProductsOnly.length;
  const totalPackAthlete = qtyPackAthlete * album.pack_athlete_unit_price;

  const packAthlete = {
    quantity: qtyPackAthlete,
    unitPrice: album.pack_athlete_unit_price,
    total: totalPackAthlete,
  };

  const total =
    packages.reduce((acc, f) => acc + f.packageTotal, 0) + totalPackAthlete;
  return {
    total,
    packages,
    packAthlete,
  };
};

export function numberToColor(nb) {
  const colors = [
    "#ff0000",
    "#ff8000",
    "#ffff00",
    "#80ff00",
    "#00ff00",
    "#00ff80",
    "#00ffff",
    "#0080ff",
    "#0000ff",
    "#8000ff",
    "#ff00ff",
    "#ff0080",
    "#ff8080",
    "#ffbf80",
    "#ffff80",
    "#bfff80",
    "#80ff80",
    "#80ffb3",
    "#80ffff",

    // more color
  ];
  return colors[nb % colors.length];
}

export function stringToColor(string) {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

export const stringAvatar = (name, sx = null) => {
  // if name has a space between 2 first words, we take the first letter of each word, otherwise we take the first 2 letters
  return {
    sx: {
      bgcolor: stringToColor(name),
      color: "white",
      ...sx,
    },
    children: `${name.split(" ")[0].substring(0, 1)}${
      name.split(" ").length > 1
        ? name.split(" ")[1].substring(0, 1)
        : name.split(" ")[0].substring(1, 2)
    }`,
  };
};

export const EXIF_FLASH = {
  0: "No flash",
  1: "Flash",
  5: "Flash, strobe return light not detected",
  7: "Flash, strobe return light detected",
  9: "Compulsory flash, red-eye reduction",
  13: "Compulsory flash, red-eye reduction, return light not detected",
  15: "Compulsory flash, red-eye reduction, return light detected",
  16: "No flash, compulsory",
  24: "No flash, auto",
  25: "Flash, auto",
  29: "Flash, auto, red-eye reduction",
  31: "Flash, auto, red-eye reduction, return light not detected",
  32: "Flash, auto, red-eye reduction, return light detected",
};

export const getFlashText = (flashDecimalValue) => {
  if (Object.keys(EXIF_FLASH).includes(flashDecimalValue)) {
    return EXIF_FLASH[flashDecimalValue];
  }
  return "N/A";
};

export const getExposureTime = (exposureTimeDecimal) => {
  if (!exposureTimeDecimal || exposureTimeDecimal === 0) {
    return "N/A";
  }
  return `1/${Math.round(
    1 / exposureTimeDecimal
  )} s (${exposureTimeDecimal} s)`;
};

export const getFNumber = (fNumber) => {
  if (!fNumber) {
    return "N/A";
  }
  return `f/${fNumber}`;
};

export const getFocalLength = (focalLength) => {
  if (!focalLength) {
    return "N/A";
  }
  return `${focalLength} mm`;
};

export const getAvatarUrl = (userUid, avatarId) => {
  return `https://photos.eventpxl.com/avatars/${process.env.REACT_APP_ENVIRONMENT}/${userUid}/${avatarId}`;
};

export const minificationStatus = (minification) => {
  if (minification === "minified") {
    return (
      <Chip
        size="small"
        label={
          <FormattedMessage id="import_job.minification_strategy.minified" />
        }
        chipcolor={"primary"}
      />
    );
  }
  return (
    <Chip
      size="small"
      label={
        <FormattedMessage id="import_job.minification_strategy.watermarked" />
      }
      chipcolor={"primary"}
    />
  );
};

export const bibWithMaxSize = (bib) => {
  if (!bib) {
    return "";
  }
  return (
    <Tooltip title={bib} arrow>
      {bib.length > 8 ? bib.slice(0, 10) + ".." : bib}
    </Tooltip>
  );
};

export const identificationStatus = (identification) => {
  if (!identification.bib) {
    return <FormattedMessage id="identification.face_without_bib" />;
  }
  if (identification.bib === identification.bib_set_by_manually) {
    return (
      <FormattedMessage
        id="identification.bib_set_manually"
        values={{
          bib_set_by_face_matching: identification.bib_set_by_face_matching,
        }}
      />
    );
  }
  if (
    identification.bib === identification.bib_set_by_detect_text &&
    !identification.bib_set_by_face_matching
  ) {
    if (!identification.face_id) {
      return <FormattedMessage id="identification.bib_read_without_face" />;
    } else {
      return <FormattedMessage id="identification.bib_read_with_face" />;
    }
  }

  if (
    identification.bib === identification.bib_set_by_face_matching &&
    identification.bib === identification.bib_set_by_detect_text
  ) {
    return <FormattedMessage id="identification.bib_confirmed_by_script" />;
  }

  if (
    identification.bib === identification.bib_set_by_face_matching &&
    identification.bib !== identification.bib_set_by_detect_text
  ) {
    return (
      <FormattedMessage
        id="identification.bib_updated_by_script"
        values={{
          bib_set_by_detect_text:
            identification.bib_set_by_detect_text || "N/A",
        }}
      />
    );
  }
};

export const displayContestantName = (contestant) => {
  if (!contestant) {
    return "";
  }
  if (contestant.first_name && contestant.last_name) {
    return `${contestant.last_name} ${contestant.first_name}`;
  }
};

export const identificationHistory = (identification) => {
  let history = [];
  if (identification.bib_set_by_manually) {
    history.push(`${identification.bib_set_by_manually} forcé par vous`);
  }
  if (identification.bib_set_by_face_matching) {
    history.push(`${identification.bib_set_by_face_matching} par script`);
  }
  if (identification.bib_set_by_detect_text) {
    history.push(`${identification.bib_set_by_face_matching} lû`);
  }
  return (
    <Stack direction="column" spacing={1}>
      {history.map((h) => (
        <Typography variant="caption">{h}</Typography>
      ))}
    </Stack>
  );
};

export const isFreeAlbum = (album) => {
  // Album is free when one of the album.album_packages.unit_prices is 0
  return album.album_packages.some((ap) =>
    ap.unit_prices.some((up) => Number(up.unit_price) === 0)
  );
};

export const creditIncrementType = (incrementType) => {
  return <FormattedMessage id={`credit_increments.type.${incrementType}`} />;
};
